import React, { useRef, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiCamera, FiImage, FiX, FiPlus } from 'react-icons/fi';

const MAX_PHOTOS = 4;
const SLOT_LABELS = ['Front', 'Left Side', 'Right Side', 'Close-Up'];

export default function StepPhoto({ data, onChange, onOpenPricing }) {
  const photos = data.photos
    ? data.photos
    : data.imageUrl
    ? [{ id: 'legacy-0', url: data.imageUrl, useInTimelapse: true }]
    : [];

  const [activePickerSlot, setActivePickerSlot] = useState(null);

  const cameraInputs = useRef(Array.from({ length: MAX_PHOTOS }, () => React.createRef()));
  const uploadInputs = useRef(Array.from({ length: MAX_PHOTOS }, () => React.createRef()));

  const handleFileSelected = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const isFirst = photos.length === 0;
      const newPhoto = {
        id: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        url: e.target.result,
        useInTimelapse: isFirst,
      };
      onChange({ photos: [...photos, newPhoto] });
    };
    reader.readAsDataURL(file);
    setActivePickerSlot(null);
  };

  const handleInput = (e) => {
    handleFileSelected(e.target.files?.[0] || null);
    e.target.value = '';
  };

  const deletePhoto = (id) => {
    const remaining = photos.filter(p => p.id !== id);
    const wasTimelapse = photos.find(p => p.id === id)?.useInTimelapse;
    const updated = wasTimelapse && remaining.length > 0
      ? remaining.map((p, i) => (i === 0 ? { ...p, useInTimelapse: true } : p))
      : remaining;
    onChange({ photos: updated });
  };

  const transferStar = (id) => {
    onChange({ photos: photos.map(p => ({ ...p, useInTimelapse: p.id === id })) });
  };

  return (
    <div className="space-y-5">
      {/* Section header */}
      <div>
        <h2 className="font-sans text-base font-semibold text-slate-800">Upload Today's Photos</h2>
        <p className="font-sans text-xs text-slate-500 mt-1 leading-relaxed">
          Track progress by adding consistent angles (front, side, close-up)
        </p>
      </div>

      {/* 2×2 grid */}
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: MAX_PHOTOS }).map((_, slotIndex) => {
          const photo = photos[slotIndex];
          const isPickerOpen = activePickerSlot === slotIndex;
          const label = SLOT_LABELS[slotIndex];

          if (photo) {
            return (
              <div key={photo.id} className="relative aspect-square rounded-[1.5rem] overflow-hidden glass-card">
                <img src={photo.url} alt={`skin ${slotIndex + 1}`} className="w-full h-full object-cover object-top" />

                {/* Slot label */}
                <div className="absolute top-2 left-2 bg-black/45 backdrop-blur-sm px-2.5 py-0.5 rounded-full">
                  <span className="font-sans text-xs text-white font-medium">{label}</span>
                </div>

                {/* Delete */}
                <button
                  onClick={() => deletePhoto(photo.id)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white active:bg-black/70"
                >
                  <SafeIcon icon={FiX} className="text-xs" />
                </button>

                {/* Timelapse star */}
                <button
                  onClick={() => transferStar(photo.id)}
                  className={`absolute bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full text-xs font-sans font-semibold backdrop-blur-sm transition-all flex items-center gap-1 ${
                    photo.useInTimelapse ? 'bg-amber-400/90 text-amber-900' : 'bg-black/40 text-white/80'
                  }`}
                >
                  {photo.useInTimelapse ? '⭐' : '☆'}
                  <span>TL</span>
                </button>
              </div>
            );
          }

          // Future slots beyond the next available one — visible but inactive
          if (slotIndex > photos.length) {
            return (
              <div
                key={`dim-${slotIndex}`}
                className="aspect-square rounded-[1.5rem] flex flex-col items-center justify-center gap-1.5"
                style={{ background: 'rgba(255,255,255,0.45)', border: '2px dashed #94a3b8' }}
              >
                <span className="font-sans text-xs font-medium text-slate-400">{label}</span>
              </div>
            );
          }

          // Next available slot
          return (
            <div
              key={`slot-${slotIndex}`}
              className="aspect-square rounded-[1.5rem] glass-card flex flex-col items-center justify-center gap-2"
              style={{ border: '2px dashed #94a3b8' }}
            >
              <span className="font-sans text-xs font-semibold text-slate-600 tracking-wide">{label}</span>

              {isPickerOpen ? (
                <div className="flex flex-col items-center gap-2 w-full px-3">
                  <div className="flex gap-3">
                    <button
                      onClick={() => cameraInputs.current[slotIndex].current?.click()}
                      className="flex flex-col items-center gap-1 px-3 py-2 rounded-2xl glass-input active:scale-95 transition-transform"
                    >
                      <SafeIcon icon={FiCamera} className="text-lg text-slate-700" />
                      <span className="font-sans text-xs text-slate-600">Camera</span>
                    </button>
                    <input ref={cameraInputs.current[slotIndex]} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleInput} />

                    <button
                      onClick={() => uploadInputs.current[slotIndex].current?.click()}
                      className="flex flex-col items-center gap-1 px-3 py-2 rounded-2xl glass-input active:scale-95 transition-transform"
                    >
                      <SafeIcon icon={FiImage} className="text-lg text-slate-700" />
                      <span className="font-sans text-xs text-slate-600">Upload</span>
                    </button>
                    <input ref={uploadInputs.current[slotIndex]} type="file" accept="image/*" className="hidden" onChange={handleInput} />
                  </div>
                  <button onClick={() => setActivePickerSlot(null)} className="font-sans text-xs text-slate-400 active:text-slate-600">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setActivePickerSlot(slotIndex)}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ border: '1.5px dashed #5C8A78', background: 'rgba(92,138,120,0.06)' }}
                  >
                    <SafeIcon icon={FiPlus} className="text-xl" style={{ color: '#5C8A78' }} />
                  </div>
                  <span className="font-sans text-xs text-slate-400">Tap to add photo</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {photos.length > 0 && (
        <p className="font-sans text-xs text-slate-400 text-center px-4">
          ⭐ <strong>TL</strong> marks the photo used in your timelapse. Tap to reassign.
        </p>
      )}

      {/* Glow score */}
      <div className="glass-card rounded-[2rem] p-6">
        <div className="flex justify-between items-center mb-6">
          <label className="font-sans text-sm font-semibold text-slate-800">Overall Glow</label>
          <span className="font-sans text-3xl font-medium text-slate-900">{data.glowScore}</span>
        </div>
        <input type="range" min="1" max="10" value={data.glowScore}
          onChange={(e) => onChange({ glowScore: parseInt(e.target.value) })} className="w-full" />
        <div className="flex justify-between mt-3 px-1">
          <span className="font-sans text-xs uppercase tracking-wider font-semibold text-slate-400">Rough</span>
          <span className="font-sans text-xs uppercase tracking-wider font-semibold text-slate-400">Glowing</span>
        </div>
      </div>
    </div>
  );
}
